/**
 * components/RiwayatList.js
 * Per-item history (riwayat) — calm, readable, low-density. Newest first.
 * Three tabs: Stok Pusat, Stok Cabang, and Semua (both buckets together).
 *
 * Sorted via `utils/history.js`'s `sortHistoryDesc` (Riwayat Stok ordering fix) rather than a
 * plain `date`-only sort — a transaction edit's "(dikoreksi)" reversal entry can share an
 * identical `date` with the original entry it corrects, and the shared helper's insertion-
 * order tiebreak is what keeps the correction rendering above the entry it supersedes.
 *
 * Section E3:
 * - **Click-through.** A PENJUALAN/PEMBELIAN/TRANSFER line's invoice number is a link that opens
 *   that transaction (`onOpenTransaction(txnId)`); a TRANSFORMASI line gets "Lihat transformasi"
 *   (`onOpenTransformation(id)`). The raw `ref <id>` is no longer shown. When the transaction
 *   was deleted since, the line says "(transaksi sudah dihapus)" instead of a dead link. The
 *   link is the note's own leading invoice text only when it still matches the transaction's
 *   current number (a re-minted number or an edited supplier no. leaves an old note behind) —
 *   otherwise the note stays plain and a separate "Lihat transaksi" link is shown.
 * - **Transfers name the other side (decision 2b).** In the Pusat/Cabang tabs a transfer line
 *   says where the stock went or came from: "→ Cabang MMC (+2)" / "← Pusat (−2)". The other
 *   side's change is always the mirror of this one (a transfer moves stock, it never makes any).
 * - **Semua.** Both buckets, newest first. A transfer's two entries (same transaction, item,
 *   note and date; opposite buckets; mirrored deltas) are shown as ONE line,
 *   "Pusat −2 → Cabang MMC +2" — always written from the side that went DOWN, so a reversal
 *   ("(dibatalkan)") honestly reads as stock moving back. Every other line names its bucket.
 *
 * A Transformasi line's note says "Rakit (Pusat)" / "Urai (Cabang)". Entries saved before
 * 2026-10-09 say "ASSEMBLE (Pusat)" / "TEARDOWN (…)"; they are SHOWN with Rakit/Urai too. The
 * stored note is never rewritten (riwayat is append-only) — only its display.
 */
import { useState } from "react";
import { fmtDate, fmtDelta, fmtQty } from "../utils/idGenerators";
import { sortHistoryDesc } from "../utils/history";
import { displayContact } from "../utils/contacts";
import { resolveBranchName } from "../utils/branches";

const KIND_LABEL = {
  PEMBUATAN: "Barang dibuat",
  PENYESUAIAN: "Penyesuaian stok",
  TRANSFORMASI: "Transformasi",
  PENJUALAN: "Penjualan",
  PEMBELIAN: "Pembelian",
  TRANSFER: "Transfer",
};
const TXN_KINDS = new Set(["PENJUALAN", "PEMBELIAN", "TRANSFER"]);

/** An old Transformasi note's leading "ASSEMBLE"/"TEARDOWN", shown as "Rakit"/"Urai". */
const transformasiNote = (note) => note.replace(/^ASSEMBLE\b/, "Rakit").replace(/^TEARDOWN\b/, "Urai");

/** The two entries one transfer line writes: same txn/item/note/date, other bucket, mirrored. */
const isTransferPair = (a, b) =>
  a.kind === "TRANSFER" && b.kind === "TRANSFER" && a.ref === b.ref && a.itemCode === b.itemCode &&
  a.note === b.note && a.date === b.date && a.bucket !== b.bucket && (Number(a.delta) || 0) === -(Number(b.delta) || 0);

export default function RiwayatList({
  entries = [], contactById = new Map(), branches = [], transactions = [],
  onOpenTransaction, onOpenTransformation,
}) {
  const [bucket, setBucket] = useState("PUSAT"); // "PUSAT" | "CABANG" | "SEMUA"

  if (entries.length === 0) {
    return <div className="empty-state">Belum ada riwayat untuk barang ini.</div>;
  }

  const txnById = new Map(transactions.map((t) => [t.id, t]));

  // "Pusat", or "Cabang MMC" (just "Cabang" when the transaction's branch can't be resolved).
  const bucketName = (b, txn) => {
    if (b !== "CABANG") return "Pusat";
    const name = txn && txn.branchId ? resolveBranchName(branches, txn.branchId) : null;
    return name ? `Cabang ${name}` : "Cabang";
  };

  const link = (text, onClick, testId) => (
    <button type="button" className="riwayat-link" data-testid={testId} onClick={onClick}>{text}</button>
  );

  /** The note line's text after the kind label, with the invoice number as a link when it can be. */
  const noteBody = (e) => {
    const note = e.kind === "TRANSFORMASI" ? transformasiNote(e.note || "") : (e.note || "");
    if (TXN_KINDS.has(e.kind) && e.ref && onOpenTransaction) {
      const txn = txnById.get(e.ref);
      const number = txn ? (txn.type === "PEMBELIAN" ? txn.supplierInvoiceNo : txn.invoiceNumber) : null;
      if (number && number !== "—" && note.startsWith(number)) {
        return <>{link(number, () => onOpenTransaction(txn.id), "riwayat-open-txn")}{note.slice(number.length)}</>;
      }
    }
    return note;
  };

  /** Extra meta bits: a fallback link, or the "deleted" notice. */
  const metaLink = (e) => {
    if (!e.ref) return null;
    if (TXN_KINDS.has(e.kind)) {
      const txn = txnById.get(e.ref);
      if (!txn) return <> · (transaksi sudah dihapus)</>;
      if (!onOpenTransaction) return null;
      const number = txn.type === "PEMBELIAN" ? txn.supplierInvoiceNo : txn.invoiceNumber;
      if (number && number !== "—" && (e.note || "").startsWith(number)) return null; // linked in the note
      return <> · {link("Lihat transaksi", () => onOpenTransaction(txn.id), "riwayat-open-txn")}</>;
    }
    if (e.kind === "TRANSFORMASI" && onOpenTransformation) {
      return <> · {link("Lihat transformasi", () => onOpenTransformation(e.ref), "riwayat-open-transformation")}</>;
    }
    return null;
  };

  const contactText = (e) => {
    // Defensive: contactId should always resolve (contacts are delete-blocked while
    // referenced), but a stale/unresolvable one renders nothing rather than crashing.
    const contact = e.contactId ? contactById.get(e.contactId) : null;
    // Contact model redesign leak fix: an internal contact's `.name` is a fixed,
    // never-real placeholder ("__internal__") — reading it directly would show that
    // literal string here. `displayContact` resolves the real branch name instead (or
    // "" when it genuinely can't — never the raw field).
    const name = contact ? displayContact(contact, branches) : "";
    return name ? ` · ${name}` : "";
  };

  // A transfer line in a single-bucket tab: where the stock went / came from.
  const otherSide = (e) => {
    if (e.kind !== "TRANSFER" || !(Number(e.delta) || 0)) return "";
    const txn = txnById.get(e.ref);
    const other = bucketName(e.bucket === "PUSAT" ? "CABANG" : "PUSAT", txn);
    return e.delta < 0 ? ` → ${other} (${fmtDelta(-e.delta)})` : ` ← ${other} (${fmtDelta(-e.delta)})`;
  };

  const singleRow = (e, showBucket) => {
    const positive = (e.delta || 0) >= 0;
    const txn = TXN_KINDS.has(e.kind) ? txnById.get(e.ref) : null;
    return (
      <div className="riwayat-row" key={e.id}>
        <div className={`riwayat-delta ${positive ? "riwayat-delta--pos" : "riwayat-delta--neg"}`}>
          {fmtDelta(e.delta)}
        </div>
        <div className="riwayat-main">
          <div className="riwayat-note">
            {KIND_LABEL[e.kind] || e.kind}
            {e.note ? <> — {noteBody(e)}</> : ""}
            {otherSide(e)}
          </div>
          <div className="riwayat-meta">
            {showBucket ? `${bucketName(e.bucket, txn)} · ` : ""}
            {fmtDate(e.date)}
            {metaLink(e)}
            {contactText(e)}
          </div>
        </div>
        <div className="riwayat-balance">
          {showBucket ? `Saldo ${e.bucket === "CABANG" ? "Cabang" : "Pusat"}: ` : "Saldo: "}<b>{fmtQty(e.balanceAfter)}</b>
        </div>
      </div>
    );
  };

  const pairRow = (from, to) => {
    const txn = txnById.get(from.ref);
    return (
      <div className="riwayat-row riwayat-row--pair" key={`${from.id}+${to.id}`}>
        <div className="riwayat-delta riwayat-delta--move" title="Pindah stok — total tidak berubah">⇄</div>
        <div className="riwayat-main">
          <div className="riwayat-note">
            Transfer{from.note ? <> — {noteBody(from)}</> : ""}
          </div>
          <div className="riwayat-move">
            {bucketName(from.bucket, txn)} {fmtDelta(from.delta)} → {bucketName(to.bucket, txn)} {fmtDelta(to.delta)}
          </div>
          <div className="riwayat-meta">
            {fmtDate(from.date)}
            {metaLink(from)}
          </div>
        </div>
        <div className="riwayat-balance">
          Saldo Pusat: <b>{fmtQty((from.bucket === "PUSAT" ? from : to).balanceAfter)}</b>
          <br />
          Saldo Cabang: <b>{fmtQty((from.bucket === "CABANG" ? from : to).balanceAfter)}</b>
        </div>
      </div>
    );
  };

  let rows;
  if (bucket === "SEMUA") {
    const sorted = sortHistoryDesc(entries);
    const used = new Set();
    rows = [];
    sorted.forEach((e, i) => {
      if (used.has(i)) return;
      used.add(i);
      if (e.kind === "TRANSFER") {
        const j = sorted.findIndex((o, k) => !used.has(k) && isTransferPair(e, o));
        if (j >= 0) {
          used.add(j);
          const o = sorted[j];
          const [from, to] = (Number(e.delta) || 0) <= 0 ? [e, o] : [o, e];
          rows.push(pairRow(from, to));
          return;
        }
      }
      rows.push(singleRow(e, true));
    });
  } else {
    rows = sortHistoryDesc(entries.filter((e) => e.bucket === bucket)).map((e) => singleRow(e, false));
  }

  return (
    <div className="riwayat-list">
      <div className="riwayat-toggle">
        <div className="seg">
          <button type="button" className={bucket === "PUSAT" ? "active" : ""} onClick={() => setBucket("PUSAT")}>Stok Pusat</button>
          <button type="button" className={bucket === "CABANG" ? "active" : ""} onClick={() => setBucket("CABANG")}>Stok Cabang</button>
          <button type="button" className={bucket === "SEMUA" ? "active" : ""} onClick={() => setBucket("SEMUA")}>Semua</button>
        </div>
      </div>

      <div className="riwayat-scroll">
        {rows.length === 0 ? (
          <div className="empty-state">Tidak ada riwayat di bucket ini.</div>
        ) : rows}
      </div>
    </div>
  );
}
